import { defineStore } from 'pinia';
import { useRegisterSW } from 'virtual:pwa-register/vue';
import { ref } from 'vue';
import { usePiniaRefStore } from '../composables/usePiniaRefStore';

/**
 * Client half of the update flow: it keeps the service worker registered and
 * tracks whether a new build is waiting to take over.
 *
 * The worker half lives in `src/pwa/update.ts` - it holds a new build back until
 * the update is accepted here, and turns a release the server announces over
 * push into an update check, so both paths end on the same waiting worker.
 */
export const usePwaStore = defineStore('pwa', () => {
  /** Handed out once, through `onRegisteredSW`, but needed for later update checks. */
  const registration = ref<ServiceWorkerRegistration>();
  const updateAvailable = ref(false);
  const updateAvailableHandlers = ref<(() => void)[]>([]);
  /**
   * Set once the restart is under way and never cleared - both paths out of
   * `applyUpdate` end in a reload, so the flag lives until the page is gone.
   * The UI shows the splash screen while it is set.
   */
  const updating = ref(false);

  const { updateServiceWorker } = useRegisterSW({
    immediate: true,
    onNeedRefresh: () => {
      updateAvailable.value = true;
      updateAvailableHandlers.value.forEach((handler) => handler());
    },
    onRegisteredSW: (_swUrl, swRegistration) => {
      registration.value = swRegistration;
    },
  });

  /** Re-fetch the worker; a changed build installs and lands in `waiting`. */
  const checkForUpdate = async () => {
    try {
      await registration.value?.update();
    } catch (err) {
      console.warn('Failed to check for a service worker update: ', err);
    }
  };

  /**
   * Hand over to the waiting build and reload. Without one - a reload asked for
   * before the new worker installed - a check is made first, since reloading on
   * the old worker would just serve the precached shell again.
   */
  const applyUpdate = async () => {
    updating.value = true;

    if (!updateAvailable.value) await checkForUpdate();

    if (updateAvailable.value) await updateServiceWorker(true);
    else window.location.reload();
  };

  return {
    registration,
    updateAvailable,
    updateAvailableHandlers,
    updating,
    checkForUpdate,
    applyUpdate,
  };
});

export const usePwa = () => usePiniaRefStore(usePwaStore());
